/* ============================================================
   UPSELL ENGINE — Dawn Pro (v2, scalable tag-based)
   Nguồn upsell: TAG (section rendering) → COLLECTION → S&D.
   Không giới hạn số sản phẩm — Shopify lọc server-side.
   ============================================================ */

/* ---------- CartAPI ---------- */
const CartAPI = {
  async add(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) throw new Error('add failed');
    await this.refreshDrawer();
    return res.json();
  },
  async get() { return (await fetch('/cart.js')).json(); },
  async refreshDrawer() {
    try {
      const res = await fetch(`${location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await res.json();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const inner = html.querySelector('cart-drawer');
        if (inner) { drawer.innerHTML = inner.innerHTML; drawer.classList.remove('is-empty'); }
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        bubble.innerHTML = new DOMParser()
          .parseFromString(sections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section').innerHTML;
      }
      if (drawer && typeof drawer.open === 'function') drawer.open();
      document.dispatchEvent(new CustomEvent('upsell:cart-updated'));
    } catch (e) {}
  },
  money(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    });
  }
};
window.UpsellCartAPI = CartAPI;

/* ---------- Helpers ---------- */
function lazyInit(el, cb, margin = '200px') {
  if (!('IntersectionObserver' in window)) { cb(); return; }
  const obs = new IntersectionObserver((en) => {
    if (en[0].isIntersecting) { obs.disconnect(); cb(); }
  }, { rootMargin: margin });
  obs.observe(el);
}

/** Tag → URL handle: "Văn Nguyen" → "van-nguyen" */
function handleize(tag) {
  return tag.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Nguồn sản phẩm dùng chung — priority: tags → collection → S&D.
 * opts: { tags: [], collection: '', productId, excludeIds: [], limit }
 */
async function fetchUpsellProducts(opts) {
  const limit = opts.limit || 4;
  const excluded = new Set(opts.excludeIds || []);
  const out = [];
  const push = (list) => {
    for (const p of list) {
      if (out.length >= limit) break;
      if (!p.available || excluded.has(p.id)) continue;
      if (out.some(o => o.id === p.id)) continue;
      out.push(p);
    }
  };
  const fromSection = async (url) => {
    try {
      const r = await fetch(url);
      if (!r.ok) return [];
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const json = doc.querySelector('[data-upsell-json]');
      return json ? JSON.parse(json.textContent) : [];
    } catch { return []; }
  };

  // 1) TAG — thử tối đa 3 tag đầu (Shopify lọc server-side, scale vô hạn)
  for (const tag of (opts.tags || []).slice(0, 3)) {
    if (out.length >= limit) break;
    const h = handleize(tag);
    if (h) push(await fromSection(`/collections/all/${h}?section_id=upsell-grid`));
  }

  // 2) COLLECTION
  if (out.length < limit && opts.collection) {
    push(await fromSection(`/collections/${opts.collection}?section_id=upsell-grid`));
  }

  // 3) S&D recommendations
  if (out.length < limit && opts.productId) {
    try {
      const r = await fetch(`/recommendations/products.json?product_id=${opts.productId}&limit=${limit}&intent=complementary`);
      const products = (await r.json()).products || [];
      push(products.map(p => ({
        id: p.id, handle: p.handle, title: p.title, url: `/products/${p.handle}`,
        image: (p.featured_image || '').replace(/(\.[^.]+)$/, '_240x$1'),
        available: true, price: p.variants[0].price, variant_id: p.variants[0].id,
        has_only_default_variant: p.variants.length <= 1,
        variants: p.variants.map(v => ({ id: v.id, title: v.title, price: v.price, available: v.available !== false }))
      })));
    } catch {}
  }
  return out;
}

function upsellCardHTML(p) {
  return `
  <div class="upsell-card">
    <a href="${p.url}" class="upsell-card__media">
      ${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="120" height="120">` : ''}
    </a>
    <div class="upsell-card__info">
      <a href="${p.url}" class="upsell-card__title">${p.title}</a>
      <span class="upsell-card__price">${CartAPI.money(p.price)}</span>
      ${!p.has_only_default_variant ? `
        <select class="upsell-card__variant" data-upsell-variant>
          ${p.variants.map(v => `<option value="${v.id}" ${v.available ? '' : 'disabled'}>${v.title}</option>`).join('')}
        </select>` : ''}
      <button type="button" class="upsell-card__add" data-upsell-add data-vid="${p.variant_id}">+ Add</button>
    </div>
  </div>`;
}

/* ============================================================
   <sticky-atc-pro>
   ============================================================ */
class StickyAtcPro extends HTMLElement {
  connectedCallback() {
    this.mainBtn = document.getElementById(this.dataset.watch);
    if (!this.mainBtn) return;
    const io = new IntersectionObserver((en) => {
      this.classList.toggle('is-visible', !en[0].isIntersecting);
    }, { threshold: 0 });
    io.observe(this.mainBtn);

    this.select = this.querySelector('[data-sticky-variant]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    if (this.select) {
      this.select.addEventListener('change', () => {
        const opt = this.select.selectedOptions[0];
        if (this.priceEl && opt.dataset.price) this.priceEl.textContent = CartAPI.money(+opt.dataset.price);
        const mainForm = document.querySelector('form[id^="product-form-"]');
        const idInput = mainForm?.querySelector('[name="id"]');
        if (idInput) idInput.value = this.select.value;
        const url = new URL(location);
        url.searchParams.set('variant', this.select.value);
        history.replaceState({}, '', url);
      });
    }

    this.querySelector('[data-sticky-add]')?.addEventListener('click', (e) => {
      const btn = e.currentTarget;
      const req = document.querySelector('.product-form__personalized-name .field__input[required]');
      if (req && !req.value.trim()) {
        req.style.borderColor = '#e22120';
        req.scrollIntoView({ behavior: 'smooth', block: 'center' });
        req.focus({ preventScroll: true });
        return;
      }
      btn.disabled = true;
      btn.textContent = btn.dataset.adding || 'Adding…';
      this.mainBtn.click();
      setTimeout(() => { btn.disabled = false; btn.textContent = btn.dataset.label || 'Add to cart'; }, 1200);
    });

    const strip = this.querySelector('upsell-recs');
    if (strip) lazyInit(this, () => strip.load?.(), '0px');
  }
}

/* ============================================================
   <upsell-recs> — tag → collection → S&D
   ============================================================ */
class UpsellRecs extends HTMLElement {
  connectedCallback() { lazyInit(this, () => this.load()); }

  async load() {
    if (this.loaded) return;
    this.loaded = true;

    const tags = (this.dataset.tags || '').split(',').map(t => t.trim()).filter(t => t && !t.startsWith('badge__'));
    let excludeIds = (this.dataset.exclude || '').split(',').map(Number).filter(Boolean);
    if (this.dataset.productId) excludeIds.push(+this.dataset.productId);
    try { excludeIds = excludeIds.concat((await CartAPI.get()).items.map(i => i.product_id)); } catch {}

    const products = await fetchUpsellProducts({
      tags,
      collection: this.dataset.collection || '',
      productId: this.dataset.productId,
      excludeIds,
      limit: +(this.dataset.limit || 4)
    });

    if (!products.length) { this.hidden = true; this.closest('.cd-upsell, .satc__recs')?.setAttribute('hidden', ''); return; }
    const grid = this.querySelector('[data-recs-grid]') || this;
    grid.innerHTML = products.map(upsellCardHTML).join('');
    this.wire();
  }

  wire() {
    this.querySelectorAll('[data-upsell-add]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const card = btn.closest('.upsell-card');
        const sel = card?.querySelector('[data-upsell-variant]');
        const id = +(sel ? sel.value : btn.dataset.vid);
        btn.disabled = true; btn.textContent = '…';
        try { await CartAPI.add([{ id, quantity: 1 }]); btn.textContent = '✓'; }
        catch { btn.textContent = '!'; }
        setTimeout(() => { btn.disabled = false; btn.textContent = '+ Add'; }, 1200);
      });
    });
  }
}

/* ============================================================
   <fbt-block> — auto theo tag qua section rendering (scale vô hạn)
   ============================================================ */
class FbtBlock extends HTMLElement {
  connectedCallback() { lazyInit(this, () => this.setup()); }

  async setup() {
    if (this.querySelectorAll('[data-fbt-item]').length > 1) { this.init(); return; }

    const tags = (this.dataset.autoTags || '').split(',').map(t => t.trim()).filter(t => t && !t.startsWith('badge__'));
    if (!tags.length) { this.hide(); return; }

    const products = await fetchUpsellProducts({
      tags,
      productId: this.dataset.productId,
      excludeIds: [+this.dataset.productId],
      limit: +(this.dataset.max || 3)
    });
    if (!products.length) { this.hide(); return; }

    const precheck = this.dataset.precheck === 'true';
    const wrap = this.querySelector('.fbt__items');
    products.forEach(p => {
      wrap.insertAdjacentHTML('beforeend', `
        <label class="fbt__item" data-fbt-item data-variant-id="${p.variant_id}" data-price="${p.price}">
          <input type="checkbox" class="fbt__check" data-fbt-check ${precheck ? 'checked' : ''}>
          ${p.image ? `<img class="fbt__img" src="${p.image}" alt="" width="56" height="56" loading="lazy">` : ''}
          <span class="fbt__info">
            <a class="fbt__title" href="${p.url}">${p.title}</a>
            <span class="fbt__price">${CartAPI.money(p.price)}</span>
          </span>
          ${!p.has_only_default_variant ? `
            <select class="fbt__variant" data-fbt-variant onclick="event.preventDefault()">
              ${p.variants.map(v => `<option value="${v.id}" data-price="${v.price}" ${v.available ? '' : 'disabled'}>${v.title}</option>`).join('')}
            </select>` : ''}
        </label>`);
    });
    this.init();
  }

  hide() { this.closest('.shopify-section, .page-width')?.setAttribute('hidden', ''); this.hidden = true; }

  init() {
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.addBtn = this.querySelector('[data-fbt-add-all]');
    this.boxes = [...this.querySelectorAll('[data-fbt-check]')];
    this.boxes.forEach(b => b.addEventListener('change', () => this.recalc()));
    this.querySelectorAll('[data-fbt-variant]').forEach(s => s.addEventListener('change', () => this.recalc()));
    this.addBtn?.addEventListener('click', () => this.addAll());
    this.recalc();
  }

  selected() {
    return this.boxes.filter(b => b.checked).map(b => {
      const row = b.closest('[data-fbt-item]');
      const sel = row.querySelector('[data-fbt-variant]');
      const opt = sel ? sel.selectedOptions[0] : null;
      return { id: +(opt ? opt.value : row.dataset.variantId), price: +(opt?.dataset.price ?? row.dataset.price) };
    });
  }

  recalc() {
    const items = this.selected();
    if (this.totalEl) this.totalEl.textContent = CartAPI.money(items.reduce((s, i) => s + i.price, 0));
    if (this.addBtn) this.addBtn.disabled = items.length === 0;
  }

  async addAll() {
    const items = this.selected().map(i => ({ id: i.id, quantity: 1 }));
    if (!items.length) return;
    this.addBtn.disabled = true;
    const orig = this.addBtn.textContent;
    this.addBtn.textContent = 'Adding…';
    try { await CartAPI.add(items); this.addBtn.textContent = '✓ Added to cart'; }
    catch { this.addBtn.textContent = 'Error — try again'; }
    setTimeout(() => { this.addBtn.textContent = orig; this.recalc(); }, 1500);
  }
}

/* ============================================================
   <rewards-bar>
   ============================================================ */
class RewardsBar extends HTMLElement {
  connectedCallback() {
    try { this.tiers = JSON.parse(this.dataset.tiers); } catch { this.tiers = []; }
    this.tiers.sort((a, b) => a.amount - b.amount);
    this.fill = this.querySelector('[data-rewards-fill]');
    this.msg = this.querySelector('[data-rewards-msg]');
    this.update(+this.dataset.cartTotal || 0);
    document.addEventListener('upsell:cart-updated', () => this.refresh());
  }
  async refresh() { try { this.update((await CartAPI.get()).total_price); } catch {} }
  update(total) {
    if (!this.tiers.length) return;
    const max = this.tiers[this.tiers.length - 1].amount;
    if (this.fill) this.fill.style.width = Math.min(100, (total / max) * 100) + '%';
    const next = this.tiers.find(t => total < t.amount);
    if (this.msg) {
      this.msg.innerHTML = next
        ? `Add <strong>${CartAPI.money(next.amount - total)}</strong> more to unlock <strong>${next.label}</strong> ${next.icon || ''}`
        : `🎉 You've unlocked <strong>all rewards!</strong>`;
    }
    this.querySelectorAll('[data-rewards-dot]').forEach(d => d.classList.toggle('is-hit', total >= +d.dataset.amount));
  }
}

if (!customElements.get('sticky-atc-pro')) customElements.define('sticky-atc-pro', StickyAtcPro);
if (!customElements.get('upsell-recs')) customElements.define('upsell-recs', UpsellRecs);
if (!customElements.get('fbt-block')) customElements.define('fbt-block', FbtBlock);
if (!customElements.get('rewards-bar')) customElements.define('rewards-bar', RewardsBar);
